/* eslint-disable max-lines */
import type { Styles } from '@adornis/ass/style.js';
import { A } from '@adornis/base/env-info.js';
import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { ColorDefinitions } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { type Category } from '@adornis/buchungsheld-db/entities/category.js';
import { Certificate } from '@adornis/buchungsheld-db/entities/certificate.js';
import {
  Einzelberatung,
  EinzelberatungSlot,
  EinzelberatungSlotConfiguration,
} from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { CertificateAttachedProduct } from '@adornis/buchungsheld-db/entities/products.js';
import { Setting } from '@adornis/buchungsheld-db/entities/settings.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/components/tr-tinymce.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import '@adornis/calendar/calendar.js';
import { XCalendar } from '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/popover/x-dropdown-selection.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime, Interval, type DateObjectUnits } from 'luxon';
import { BehaviorSubject, combineLatest, filter, map, of, switchMap } from 'rxjs';
import '../../../../components/bh-category-picker.js';
import '../../../../components/bh-product-certificate-picker.js';
import { BHFormController } from '../../../../controller/BHFormController.js';
import { BHPromptUserPicker } from '../../../../prompts/bh-prompt-user-picker.js';

function sameSlot(s1: EinzelberatungSlot, s2: EinzelberatungSlot) {
  if (!s1.start) throw new Error('s1 start is not set');
  if (!s2.start) throw new Error('s2 start is not set');
  if (!s1.end) throw new Error('s1 end is not set');
  if (!s2.end) throw new Error('s2 end is not set');

  return (
    s1.beraterID === s2.beraterID &&
    s1.start.toFormat('dd.LL.yyyy hh:mm') === s2.start.toFormat('dd.LL.yyyy hh:mm') &&
    s1.end.toFormat('dd.LL.yyyy hh:mm') === s2.end.toFormat('dd.LL.yyyy hh:mm')
  );
}

@customElement('bh-page-admin-einrichtung-counseling-modify')
export class BHPageAdminEinrichtungCounselingModify extends BuchungsheldLitElement {
  @property({ attribute: 'counseling-id' }) readonly counselingID = new RXController<Maybe<Einzelberatung>>(
    this,
    undefined,
  );
  @property({ attribute: 'mode' }) mode: 'add' | 'edit' | undefined = undefined;
  @property({ attribute: 'institution-id' }) readonly institutionID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly counseling = new RXController(
    this,
    combineLatest([this.institutionID.observable, this.counselingID.observable]).pipe(
      filter(([institutionID]) => Boolean(institutionID)),
      switchMap(async ([institutionID, counselingID]) => {
        institutionID = institutionID as string;

        if (counselingID) {
          const eb = await getByID(
            Einzelberatung,
            counselingID as any,
          )({
            ...Einzelberatung.allFields,
            attachedCertificate: CertificateAttachedProduct.allFields,
            slotConfig: EinzelberatungSlotConfiguration.allFields,
            settings: { ...Setting.allFields, ColorDefinitions: ColorDefinitions.allFields },
          });

          return eb as Einzelberatung;
        }

        const settings = await Setting.getNewProductSetting(institutionID)({
          ...Setting.allFields,
          colorDefinitions: ColorDefinitions.allFields,
        });

        return new Einzelberatung({
          _id: counselingID,
          institutionID,
          settings,
          slotConfig: null,
        });
      }),
      map(counseling => {
        if (!counseling.slotConfig) {
          const now = DateTime.now();
          const start = DateTime.now().set({ day: now.day + 1, hour: 10, minute: 0 });
          const end = DateTime.now().set({ day: now.day + 1, hour: 14, minute: 0 });
          const slotDuration = 30;
          counseling.slotConfig = new EinzelberatungSlotConfiguration({
            start,
            end,
            slotDuration,
            beraterIDs: [],
          });
        }

        return counseling;
      }),
    ),
  );

  @state() private readonly _timeframes = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => Timeframe.getTimeframesByInstitution(institutionID)(Timeframe.allFields)),
    ),
  );

  @state() private readonly _certificates = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => Certificate.getCertificatesByInstitution(institutionID)(Certificate.allFields)),
    ),
  );

  @state() private categoryError: Maybe<string>;
  @state() private dayError: Maybe<string>;

  @state() private readonly _counselingController = new BHFormController(
    this,
    this.counseling.observable.pipe(filter(Boolean)),
    {
      success: async doc => {
        if (this.mode === 'add') {
          await XDialog.guard(async () => {
            const productID = await doc.create();
            doc.settings.productID = productID;
            await doc.settings.save();
          });
        } else if (this.mode === 'edit') {
          await XDialog.guard(async () => {
            await doc.save();
            await doc.settings.save();
          });
        }

        void XSnackbar.show((this._translation.translate('saved') as string) + '!');
        goTo(`/admin/institution-details/${this.institutionID.value}/counselings`);
      },
      error: err => {
        console.log(err);
        // TODO: find a way to set them automatically
        // manually set error messages
        const errRequired = this._translation.translate('validation_non_optional') as string;
        const inErr = (msg: string) => err.message.includes(msg);
        this.categoryError = inErr('categoryID') ? errRequired : undefined;
        this.dayError = inErr('Key start is required') ? errRequired : undefined;

        const message = (() => {
          if (err.extensions.translationKey === 'validation_non_optional') {
            return this._translation.translate('validation_required_fields_missing');
          }

          if (err.message.includes('ValidationError')) {
            return this._translation.translate('check_form');
          }

          return err.message;
        })();

        return XSnackbar.show(message);
      },
    },
  );

  @state() private _selectedBeraterTab = '';

  get beraterIDs() {
    return this._counselingController.document?.slotConfig?.beraterIDs ?? [];
  }

  private slotsMarkedForDeletion: EinzelberatungSlot[] = [];
  get currentlyPersistedSlots() {
    return this.counseling.value?.slots.map(sl => sl._id) ?? [];
  }

  private newBeraters = new BehaviorSubject<User[]>([]);

  private berators = new RXController(
    this,
    combineLatest([
      this.newBeraters,
      this.counseling.observable.pipe(
        switchMap(counseling => {
          if (!counseling) return of([]);
          const berator = counseling.slotConfig?.beraterIDs ?? [];
          return getByID(User, berator)({ _id: 1, _class: 1, firstName: 1, lastName: 1, email: 1, title: 1 });
        }),
      ),
    ]).pipe(map(([newBerators, loadedBerators]) => [...newBerators, ...loadedBerators])),
  );

  calendarReference = createRef<XCalendar>();

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.counseling.value?.revert();
    // TODO: without this line document has still previous used data;
    //       So it does not get cleared automatically for some reason
    this._counselingController.document = null;
    this.slotsMarkedForDeletion = [];
  }

  override render() {
    if (this.counseling.isLoading || !this.counseling.value || !this._counselingController.document) return nothing;

    setTimeout(() => {
      const start = this._counselingController.document?.slotConfig?.start?.toJSDate();
      if (start) this.calendarReference.value?.calendarInstance?.gotoDate(start);
    }, 20);

    return html` ${this._templateContent()} `;
  }

  // eslint-disable-next-line max-lines-per-function
  private _templateContent() {
    const counseling = this._counselingController.document;
    if (!counseling || !this.institutionID.value) return nothing;

    const settings = counseling.settings;
    const showInherited = (iv: [string, string | number | boolean] | undefined) => {
      return iv ? `${this._translation.translate('settings', 'defaults', iv[0] as any) as string}: ${iv[1]}` : '';
    };

    const paymentPlugin = getPluginForScope('payment', this.institutionID.value);
    const availablePaymentMethods = paymentPlugin.paymentMethods;
    if (!counseling.settings.paymentMethods) {
      // @ts-expect-error set default to everthing activated
      counseling.settings.doc.paymentMethods = availablePaymentMethods;
    }

    this.exposeAction('save', {
      icon: 'save',
      text: this._translation.translate('save'),
      callback: () => this._counselingController.validate(),
    });

    return html`
      <x-flex space="md">
        <x-flex space="md" horizontal>
          <tr-input
            flex
            ${this._counselingController.field('name')}
            placeholder="${this._translation.translate('field', 'name')}*"
          ></tr-input>

          <tr-input
            type="number"
            ${this._counselingController.field('sortingWeight')}
            placeholder=${this._translation.translate('field', 'sortingWeight')}
            info=${this._translation.translate('fieldInfo', 'sortingWeight')}
          ></tr-input>
        </x-flex>

        <bh-admin-multi-lang-text
          label="${this._translation.translate('field', 'description')}*"
          ${this._counselingController.field('translatedDescriptionHTML')}
        ></bh-admin-multi-lang-text>

        <x-input
          type="date"
          ${this._counselingController.field('publishDate')}
          placeholder="${this._translation.translate('field', 'publishDate')}"
        ></x-input>

        <tr-input
          flex
          type="text"
          ${this._counselingController.field('topic')}
          placeholder="${this._translation.translate('field', 'topic')}"
        ></tr-input>

        <x-dropdown-selection
          ${this._counselingController.field('presenceType')}
          placeholder=${this._translation.translate('PRODUCT', 'presenceType')}
          .selectables=${['not_selected', 'in_person', 'online', 'hybrid'] as any}
          .renderString=${item => this._translation.translate('PRODUCT', 'presenceTypes', item)}
        ></x-dropdown-selection>

        <x-dropdown-selection
          ${this._counselingController.field('language')}
          placeholder=${this._translation.translate('PRODUCT', 'language')}
          .selectables=${['de', 'en'] as any}
          .renderString=${item => this._translation.translate('PRODUCT', 'languages', item)}
        ></x-dropdown-selection>

        <x-flex horizontal crossaxis-center space="md">
          <x-checkbox
            label=${this._translation.translate('field', 'hasPrice')}
            ${this._counselingController.field('hasPrice')}
            @value-picked=${(e: ValueEvent<boolean>) => {
              if (e.detail.value && this._counselingController.document?.prices[0]?.unitAmount) {
                this._counselingController.document.prices[0].unitAmount = 0;
              }
              this.requestUpdate();
            }}
          ></x-checkbox>

          ${this._counselingController.document?.hasPrice
            ? html`
                ${paymentPlugin.priceGroups.map(
                  (priceGroup, i) => html`
                    <tr-formfield-wrapper
                      placeholder=${this._translation.translate('field', 'unitAmount') +
                      (paymentPlugin.priceGroups.length > 1 ? ` (${priceGroup})` : '')}
                    >
                      <tr-currency-input
                        ${this._counselingController.field('prices', i)}
                        default=${0}
                      ></tr-currency-input>
                    </tr-formfield-wrapper>
                  `,
                )}
                ${availablePaymentMethods.length > 1
                  ? html` <x-dropdown-multiselection
                      ${this._counselingController.field('settings', 'paymentMethods')}
                      placeholder=${this._translation.translate('PAYMENT', 'method')}
                      .selectables=${availablePaymentMethods}
                      .renderString=${item => this._translation.translate('PAYMENT', 'methods', item)}
                    ></x-dropdown-multiselection>`
                  : nothing}
              `
            : nothing}
        </x-flex>

        <!-- categories -->
        <bh-category-picker
          flex
          institution-id=${this.institutionID.value}
          .value=${counseling.category}
          error=${ifDefined(this.categoryError) as string}
          @value-picked=${(e: ValueEvent<Maybe<Category>>) => {
            const categoryID = e.detail.value?._id;
            if (!categoryID) return;

            const doc = this._counselingController.document;
            if (!doc) throw new Error('should not happen');
            doc.category = e.detail.value;

            // TODO category shouldn't be required but it is atm
            counseling.categoryID = categoryID;
            this.requestUpdate();
          }}
        >
        </bh-category-picker>
        <!-- timeframe -->
        <x-dropdown-selection
          .emptyState=${this._translation.translate('dropdowns', 'emptyStateTimeframes')}
          clearable
          .value=${this._timeframes.value?.find(timeframe => timeframe._id === this.counseling.value?.timeframeID)}
          placeholder=${this._translation.translate('timeframe', 'singular')}
          .selectables=${this._timeframes.value}
          @value-picked=${(e: ValueEvent<Maybe<Timeframe>>) => {
            counseling.timeframeID = e.detail.value?._id ?? null;
          }}
          .renderString=${(instance: Timeframe) => instance.name}
        >
        </x-dropdown-selection>

        <!-- certificate -->
        <tr-flex>
          <bh-product-certificate-picker
            institution-id=${this.institutionID.value}
            ${this._counselingController.field('attachedCertificate')}
          ></bh-product-certificate-picker>
        </tr-flex>

        <tr-input
          ${this._counselingController.field('settings', 'courseLocation')}
          type="text"
          placeholder=${this._translation.translate('settings', 'course_location')}
          realPlaceholder=${showInherited(settings.inheritedCourseLocation)}
        ></tr-input>
        <x-flex horizontal space="md">
          <tr-input
            flex
            ${this._counselingController.field('settings', 'blockSignOffDaysInAdvance')}
            type="number"
            step="1"
            placeholder=${this._translation.translate('settings', 'cancel_deadline')}
            realPlaceholder=${showInherited(settings.inheritedBlockSignOffDaysInAdvance)}
            info=${this._translation.translate('fieldInfo', 'institution', 'cancellationDeadline')}
          ></tr-input>
          <tr-input
            flex
            ${this._counselingController.field('settings', 'blockAutomaticMoveUpDaysInAdvance')}
            type="number"
            step="1"
            placeholder=${this._translation.translate('settings', 'move_up_notice')}
            realPlaceholder=${showInherited(settings.inheritedBlockAutomaticMoveUpDaysInAdvance)}
            info=${this._translation.translate('fieldInfo', 'institution', 'waitinglistDeadline')}
          ></tr-input>
        </x-flex>
        <x-flex horizontal space="md">
          <tr-input
            flex
            type="number"
            step="1"
            info=${this._translation.translate('fieldInfo', 'institution', 'firstReminderMail')}
            placeholder=${this._translation.translate('settings', 'reminder_mail_1')}
            realPlaceholder=${showInherited(settings.inheritedFirstReminderLeadTime)}
            ${this._counselingController.field('settings', 'firstReminderLeadTime')}
          ></tr-input>
          <tr-input
            flex
            type="number"
            step="1"
            info=${this._translation.translate('fieldInfo', 'institution', 'secondReminderMail')}
            placeholder=${this._translation.translate('settings', 'reminder_mail_2')}
            realPlaceholder=${showInherited(settings.inheritedSecondReminderLeadTime)}
            ${this._counselingController.field('settings', 'secondReminderLeadTime')}
          ></tr-input>
        </x-flex>

        <!-- infoLink -->
        <tr-input
          ${this._counselingController.field('settings', 'infoLink')}
          placeholder="Infolink"
          realPlaceholder=${showInherited(settings.inheritedInfoLink)}
        ></tr-input>

        <!-- hier kommt der richtige Einzelberatungsteil hin -->
        <x-hr></x-hr>

        <!-- start of metadata -->
        <x-flex space="sm">
          <tr-h4 bold> ${this._translation.translate('counseling', 'possible_timeslots')} </tr-h4>
          <tr-date-picker-dropdown
            placeholder="Tag auswählen"
            error=${ifDefined(this.dayError) as string}
            .value=${counseling.slotConfig!.start}
            @value-picked=${(e: ValueEvent<DateTime>) => {
              const pickedDate = e.detail.value;
              this.updateTime('date', { year: pickedDate.year, month: pickedDate.month, day: pickedDate.day });
            }}
          ></tr-date-picker-dropdown>

          <!-- start/end -->
          <tr-flex horizontal space="md">
            <tr-flex flex space="xxs">
              <tr-text> ${this._translation.translate('from')} </tr-text>

              <x-input
                type="time"
                .value=${counseling.slotConfig!.start}
                @value-picked=${(e: ValueEvent<DateTime>) => {
                  const v = e.detail.value;
                  this.updateTime('start', { hour: v.hour, minute: v.minute });
                }}
              ></x-input>
            </tr-flex>

            <tr-flex flex space="xxs">
              <tr-text> ${this._translation.translate('till')} </tr-text>

              <x-input
                type="time"
                .value=${counseling.slotConfig!.end}
                @value-picked=${(e: ValueEvent<DateTime>) => {
                  const v = e.detail.value;
                  this.updateTime('end', { hour: v.hour, minute: v.minute });
                }}
              ></x-input>
            </tr-flex>
          </tr-flex>

          <tr-input
            type="number"
            placeholder="Dauer in Minuten pro Slot"
            postfix="Min."
            ${this._counselingController.field('slotConfig', 'slotDuration')}
          ></tr-input>

          <!-- lunch break -->
          <x-flex flex space="md">
            <x-checkbox
              label=${this._translation.translate('counseling', 'lunchbreak')}
              .value=${this._counselingController.document?.slotConfig?.hasLunchBreak ?? false}
              @value-picked=${(e: ValueEvent<boolean>) => {
                const doc = this._counselingController.document;
                if (!doc) throw new Error('should not happen');
                doc.slotConfig!.hasLunchBreak = e.detail.value;
                this.requestUpdate();
              }}
            ></x-checkbox>

            ${counseling.slotConfig!.hasLunchBreak
              ? html`
                  <tr-flex horizontal space="md">
                    <tr-flex flex space="xxs">
                      <tr-text>
                        ${this._translation.translate('counseling', 'lunchbreak')}
                        ${this._translation.translate('from')}
                      </tr-text>

                      <x-input
                        type="time"
                        .value=${counseling.slotConfig!.startLunch}
                        @value-picked=${(e: ValueEvent<DateTime>) => {
                          const v = e.detail.value;
                          this.updateTime('startLunch', { hour: v.hour, minute: v.minute });
                        }}
                      ></x-input>
                    </tr-flex>

                    <tr-flex flex space="xxs">
                      <tr-text>
                        ${this._translation.translate('counseling', 'lunchbreak')}
                        ${this._translation.translate('till')}
                      </tr-text>

                      <x-input
                        type="time"
                        .value=${counseling.slotConfig!.endLunch}
                        @value-picked=${(e: ValueEvent<DateTime>) => {
                          const v = e.detail.value;
                          this.updateTime('endLunch', { hour: v.hour, minute: v.minute });
                        }}
                      ></x-input>
                    </tr-flex>
                  </tr-flex>
                `
              : nothing}
          </x-flex>
        </x-flex>

        <!-- end of metadata -->

        ${this.renderSlots()}
      </x-flex>
    `;
  }

  private renderSlots() {
    const selectedBerater = (() => {
      if (this._selectedBeraterTab) return this._selectedBeraterTab;
      if (this.beraterIDs.length > 0) return this.beraterIDs[0];
      return null;
    })();

    const beraters = this.berators.value ?? [];
    const reference = createRef<XDropdown>();
    const tabs = html`
      <x-flex ${css({ position: 'relative' })}>
        <x-tabs .selected=${selectedBerater} ${css({ borderBottom: `1px solid ${this.colors.neutral}` })}>
          ${this.beraterIDs.map(beraterID => {
            const berater = beraters.find(b => b._id === beraterID);
            return html`
              <x-tab label=${beraterID} @click=${() => (this._selectedBeraterTab = beraterID)}>
                ${berater?.firstName ?? ''} ${berater?.lastName ?? ''}
              </x-tab>
            `;
          })}
        </x-tabs>

        <x-dropdown ${ref(reference)} ${css({ position: 'absolute', right: '0' })}>
          <tr-button-small icon="menu" @click=${() => reference.value?.toggle()}></tr-button-small>
          <x-flex slot="dropdown-content">
            <x-card>
              ${this.beraterIDs.length === 0
                ? nothing
                : html`
                    ${this.beraterIDs.map(beraterID => {
                      const berater = beraters.find(b => b._id === beraterID);
                      return html`
                        <x-button
                          mode="text"
                          class="hover-text-button"
                          icon="delete"
                          @click=${async () => this.removeBerater(beraterID)}
                        >
                          ${berater?.firstName ?? ''} ${berater?.lastName ?? ''}
                        </x-button>
                      `;
                    })}

                    <x-flex
                      ${css({
                        height: '1px',
                        width: '100%',
                        background: this.colors.secondary.shade(100),
                      })}
                    ></x-flex>
                  `}

              <x-button mode="text" class="hover-text-button" icon="add" @click=${async () => this.addBerater()}>
                ${this._translation.translate('counseling', 'add_advisor')}
              </x-button>
            </x-card>
          </x-flex>
        </x-dropdown>
      </x-flex>
    `;

    if (this.beraterIDs.length === 0)
      return html` <x-button @click=${async () => this.addBerater()} mode="outline" ${css({ alignSelf: 'start' })}>
        ${this._translation.translate('counseling', 'add_advisor')}
      </x-button>`;

    if (!selectedBerater) throw new Error('should not happen');

    const possibleTimeSlots = this.getPossibleTimeSlots();

    const findSlot = (tSlot: { start: DateTime; end: DateTime }) =>
      this._counselingController.document?.slots.find(s1 => sameSlot(s1, { ...tSlot, beraterID: selectedBerater }));

    const calendarEvents = possibleTimeSlots.map(tSlot => {
      const active = !!findSlot(tSlot);
      return {
        id: [selectedBerater, tSlot.start.toISO(), tSlot.end.toISO()].join(';;;'),
        title: active ? '[x]' : '[_]',
        display: tSlot.start.toFormat('HH:mm') + ' - ' + tSlot.end.toFormat('HH:mm'),
        start: tSlot.start.toJSDate(),
        end: tSlot.end.toJSDate(),
        active,
        backgroundColor: active ? this.colors.accent : this.colors.accent.shade(100),
        textColor: active ? 'white' : '#7c8dae',
      };
    });

    const cardWrapper = html`
      <x-calendar
        ${css({ fontSize: '14px' })}
        ${ref(this.calendarReference)}
        week
        .events=${calendarEvents}
        .options=${{
          // TODO: this can over/underflow and lowest/highst times are not guaranted
          slotMinTime: possibleTimeSlots[0]?.start.minus({ hour: 1 }).toFormat('HH:mm'),
          slotMaxTime: possibleTimeSlots.at(-1)?.start.plus({ hour: 1 }).toFormat('HH:mm'),
          initialDate: possibleTimeSlots[0]?.start.toJSDate(),
        }}
        @eventClick=${e => {
          const id = e.detail.event.id;
          const [beraterID, isoStartTime, isoEndTime] = id.split(';;;');
          const start = DateTime.fromISO(isoStartTime);
          const end = DateTime.fromISO(isoEndTime);
          const slot = findSlot({ beraterID, start, end });
          if (slot) console.log('slot found => toggle of'), this.deactivateSlot(slot);
          else console.log('slot missing => enable'), this.activateSlot(start, end, beraterID);
        }}
      ></x-calendar>
    `;

    return html`
      <x-hr></x-hr>
      <x-flex>
        ${tabs} <br />
        ${cardWrapper}
      </x-flex>
    `;
  }

  private async addBerater() {
    if (!this.institutionID.value) return XDialog.alert('fatal: missing institution ID');
    const newBerater = await BHPromptUserPicker.showPopup<Maybe<User>>({
      props: {
        institutionID: this.institutionID,
        userTypeForNewlyCreatedUser: 'lecturer',
      },
    });
    if (!newBerater) return;

    // do not add user twice
    if (this.beraterIDs.find(b => b === newBerater._id)) {
      void XSnackbar.show(this._translation.translate('counseling', 'advisor_already_added'));
      return;
    }

    this.beraterIDs.push(newBerater._id);

    // save info to show in ui
    const currentBeraters = (this.berators.value ?? []) as User[];
    this.newBeraters.next([...currentBeraters, newBerater]);

    // enable all slots by default
    for (const tSlot of this.getPossibleTimeSlots()) {
      this.activateSlot(tSlot.start, tSlot.end, newBerater._id);
    }

    this.requestUpdate();
  }

  private async removeBerater(beraterID: string) {
    const doc = this._counselingController.document;
    if (!doc) throw new Error('cant find doc');

    // remove/remeber stuff
    this.beraterIDs.splice(this.beraterIDs.indexOf(beraterID), 1);
    const slotsToRemove = doc.slots.filter(slot => slot.berater?._id === beraterID);
    doc.slots = doc.slots.filter(slot => slot.berater?._id !== beraterID);
    for (const slot of slotsToRemove) this.deactivateSlot(slot);

    this._selectedBeraterTab = this.beraterIDs[0] ?? '';

    this.requestUpdate();
  }

  private deactivateSlot(slot: EinzelberatungSlot) {
    // remeber existing slots, so that it can be restored (same id)
    if (this.currentlyPersistedSlots?.includes(slot._id)) this.slotsMarkedForDeletion.push(slot);

    // remove it from document slots
    this._counselingController.document!.slots = this._counselingController.document!.slots.filter(
      sl => sl._id !== slot._id,
    );

    this.requestUpdate();
  }

  private activateSlot(start: DateTime, end: DateTime, beraterID: string) {
    // search for maybe existing but removed slot
    const slotToRestore = this.slotsMarkedForDeletion.find(s1 => sameSlot(s1, { beraterID, start, end }));

    // add slot
    const newSlot = new EinzelberatungSlot({ _id: A.getGloballyUniqueID(), start, end, beraterID });
    this._counselingController.document?.slots.push(slotToRestore ?? newSlot);

    // cleanup
    if (slotToRestore)
      this.slotsMarkedForDeletion = this.slotsMarkedForDeletion.filter(it => it._id !== slotToRestore._id);

    this.requestUpdate();
  }

  getPossibleTimeSlots(): { start: DateTime; end: DateTime }[] {
    const slotConfig = this._counselingController.document?.slotConfig;
    if (!slotConfig) throw new Error('should not happen');

    const start = slotConfig.start;
    const end = slotConfig.end;
    const slotDuration = slotConfig.slotDuration;
    const startLunch = slotConfig.startLunch;
    const endLunch = slotConfig.endLunch;
    const hasLunchBreak =
      slotConfig.hasLunchBreak &&
      startLunch &&
      endLunch &&
      startLunch > start &&
      endLunch > startLunch &&
      endLunch < end; // ignore lunchbreak when not set correctly

    if (!slotDuration) return [];

    const slots: { start: DateTime; end: DateTime }[] = [];

    const createSlot = (start: Maybe<DateTime>) => {
      if (!start) throw new Error('start is not set');
      const end = start.plus({ minutes: slotDuration });
      return { start, end };
    };

    const addSlotsTill = (till: Maybe<DateTime>) => {
      if (!till) throw new Error('start is not set');
      while (true as boolean) {
        const lastSlot = slots.at(-1);
        const slotStart = lastSlot?.end ?? start;
        const slot = createSlot(slotStart);
        if (slot.end <= till) slots.push(slot);
        else break;
      }
    };

    if (hasLunchBreak) addSlotsTill(startLunch); // before Lunchbreak
    if (hasLunchBreak) slots.push(createSlot(endLunch)); // add first slot after lunchbreak
    addSlotsTill(end); // after Lunchbreak

    return slots;
  }

  updateTime(kind: 'date' | 'start' | 'end' | 'startLunch' | 'endLunch', changes: DateObjectUnits) {
    const previouslyPossibleTimeslots = this.getPossibleTimeSlots();

    const slotConfig = this._counselingController.document?.slotConfig;
    if (!slotConfig) throw new Error('should not happen');

    if (kind === 'date' || kind === 'start') slotConfig.start = slotConfig.start.set(changes);
    if (kind === 'date' || kind === 'end') slotConfig.end = slotConfig.end.set(changes);

    // initialize start/end-Lunch in case they are not set
    if (!(slotConfig.startLunch as any)) slotConfig.startLunch = slotConfig.start.set({});
    if (!(slotConfig.endLunch as any)) slotConfig.endLunch = slotConfig.start.set({});

    if (kind === 'date' || kind === 'startLunch') slotConfig.startLunch = slotConfig.startLunch.set(changes);
    if (kind === 'date' || kind === 'endLunch') slotConfig.endLunch = slotConfig.endLunch.set(changes);

    // correct times
    if (kind === 'start' && slotConfig.start && slotConfig.end && slotConfig.start > slotConfig.end)
      slotConfig.end = slotConfig.start;

    if (kind === 'end' && slotConfig.start && slotConfig.end && slotConfig.start > slotConfig.end)
      slotConfig.start = slotConfig.end;

    if (
      kind === 'startLunch' &&
      slotConfig.startLunch &&
      slotConfig.endLunch &&
      slotConfig.startLunch > slotConfig.endLunch
    )
      slotConfig.endLunch = slotConfig.startLunch;

    if (
      kind === 'endLunch' &&
      slotConfig.startLunch &&
      slotConfig.endLunch &&
      slotConfig.startLunch > slotConfig.endLunch
    )
      slotConfig.startLunch = slotConfig.endLunch;

    // update lunchbreak dates when not activated
    if (!slotConfig.hasLunchBreak) {
      const diff = Interval.fromDateTimes(slotConfig.start, slotConfig.end);
      const diffHours = diff.length('hours');
      slotConfig.startLunch = slotConfig.start.plus({ hours: diffHours / 2 });
      slotConfig.endLunch = slotConfig.start.plus({ hours: diffHours / 2 + 1 });
    }

    // mark all unused slots as deleted
    const availableSlots = this.getPossibleTimeSlots();
    const invalidSlots = this._counselingController.document!.slots.filter(
      s1 => !availableSlots.find(s2 => sameSlot(s1, { ...s2, beraterID: s1.beraterID })),
    );

    // remove it from document slots
    this._counselingController.document!.slots = this._counselingController.document!.slots.filter(
      s1 => !invalidSlots.find(s2 => sameSlot(s1, s2)),
    );

    // rember slots in case user wants to restore them
    const persitedInvalidSlots = invalidSlots.filter(s1 => this.currentlyPersistedSlots.find(s2 => sameSlot(s1, s2)));
    this.slotsMarkedForDeletion.push(...persitedInvalidSlots);

    // enable newly created slots
    const currentlyPossibleTimeslots = this.getPossibleTimeSlots();
    const newlyCreatedTimeSlots = currentlyPossibleTimeslots.filter(
      c_sl => !previouslyPossibleTimeslots.find(p_sl => sameSlot(c_sl, p_sl)),
    );

    for (const newTimeslot of newlyCreatedTimeSlots) {
      for (const beraterID of this.beraterIDs) {
        this.activateSlot(newTimeslot.start, newTimeslot.end, beraterID);
      }
    }

    this.requestUpdate();
    this.calendarReference.value?.calendarInstance?.gotoDate(slotConfig.start.toJSDate());
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '.hover-text-button:hover': {
          background: 'red',
        },

        // slot states
        'tr-flex[selected]': {
          background: this.colors.accent,
          borderRadius: this.sizes.borderRadius,
          color: '#fff',
        },
        'tr-flex[selected]:before': {
          content: "''",
          borderRadius: '50%',
          height: '10px',
          width: '10px',
          background: '#fff',
        },
        // synced
        'tr-flex[synced]': {
          background: this.colors.accent.shade(50),
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        // default
        'tr-flex[default]': {
          background: this.colors.tone.lightGrey,
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        'tr-flex[default]:before': {
          content: "''",
          borderRadius: '50%',
          height: '6px',
          width: '6px',
          border: `2px solid ${this.colors.accent}`,
        },
        'tr-flex[state]': {
          padding: `${this.spacing.xs} ${this.spacing.md}`,
          userSelect: 'none',
        },
        'tr-flex[clickable]': {
          cursor: 'pointer',
        },
      },
    ];
  }
}
